import { lighten } from '@mui/system'

import { STATES } from 'components/rbac-settings/teams/integrations/webhooks/webhook-utils'
import { muiTheme } from 'mui-theme'
import theme from 'theme'

import type { ButtonProps, ChipProps } from '@mui/material'
import type { ApiKeyStatus } from 'types/api/api-key'
import type { ClientState, ClientStatus } from 'types/api/attacks/client'
import type { ExperimentRunState } from 'types/api/failure-flags/failure-flags'
import type { ScenarioRunStage } from 'types/api/scenarios/runs/scenario-runs'
import type { WebhookState } from 'types/api/webhooks'

export function getColorForClientState(state: ClientState | string) {
  switch (state.toLowerCase()) {
    case 'active':
      return theme.colors.green[0]
    case 'revoked':
      return theme.colors.red[0]
    case 'idle':
      return theme.colors.gray[2]
    case 'starting':
      return theme.colors.orange[0]
    default:
      return theme.colors.steel[1]
  }
}

export function getColorForClientStatus(clientStatus: ClientStatus): string {
  switch (clientStatus) {
    case 'HEALTHY':
      return theme.colors.green[0]
    case 'UNHEALTHY':
      return theme.colors.red[0]
    case 'IMPAIRED':
      return theme.colors.orange[0]
    default:
      return theme.colors.steel[1]
  }
}

export function getColorForWebhookState(state: WebhookState): string {
  switch (state) {
    case STATES.ACTIVE:
      return theme.colors.green[0]
    case STATES.DEACTIVATED:
      return theme.colors.red[0]
    default:
      return theme.colors.steel[1]
  }
}

/**
 *
 * @param {string} state state of scenario and attack runs on scenario details
 */
export function getScenarioStateChipColor(
  state: ScenarioRunStage | undefined
): ChipProps['color'] {
  switch (state?.toLowerCase()) {
    case 'completed':
      return 'success'
    case 'failed':
    case 'setupfailed':
      return 'error'
    case 'inputresults':
      return 'warning'
    case 'active':
      return 'oceanBlue'
    default:
      return 'default'
  }
}

export function getColorForScenarioResult(
  scenarioResult: string
): ChipProps['color'] {
  switch (scenarioResult.toLowerCase()) {
    case 'passed':
      return 'success'
    case 'failed':
      return 'error'
    case 'input results':
    case 'unsure':
      return 'warning'
    default:
      return 'default'
  }
}

export function getExperimentRunStateChipColor(
  state: ExperimentRunState
): ChipProps['color'] {
  switch (state) {
    case 'COMPLETED':
      return 'success'
    case 'HALTED':
      return 'warning'
    case 'FAILED':
      return 'error'
    case 'IN_PROGRESS':
      return 'oceanBlue'
    default:
      return 'default'
  }
}

export function getColorForApiKeyState(state: ApiKeyStatus): string {
  switch (state) {
    case 'ACTIVE':
      return theme.colors.green[0]
    case 'REVOKED':
      return theme.colors.red[0]
    default:
      return theme.colors.steel[1]
  }
}

/**
 * Contains logic to get the color of a linear gradient at one particular position
 * Takes in the beginning and ending color in the Hex array and the position ratio of the color
 * compared to the width of the container with the gradient
 * @param  {[number, number, number]} color1
 * @param  {[number, number, number]} color2
 * @param  {number} percent,
 *
 * @return {string}
 */
export function getHexFromLinearGradient(
  color1: [number, number, number],
  color2: [number, number, number],
  percent: number
): string {
  const colorRatio = percent * 2 - 1
  const color1Ratio = (colorRatio / 1 + 1) / 2
  const color2Ratio = 1 - color1Ratio

  const rgb = `rgb(${Math.round(
    color1[0] * color1Ratio + color2[0] * color2Ratio
  )},${Math.round(
    color1[1] * color1Ratio + color2[1] * color2Ratio
  )},${Math.round(color1[2] * color1Ratio + color2[2] * color2Ratio)})`
  return rgb
}

// take a color from the MUI theme and lighten it a bit for use as a background color
export function getLightMuiBackgroundColor(color: string) {
  return lighten(color, 0.9)
}

export function getLightMuiBackgroundColorHover(color: string) {
  return lighten(color, 0.8)
}

/**
 * When a button that's part of a button group is selected,
 * it gets alterations of primary light background color.
 */
export function getPrimaryLightBackgroundColorsSx(
  isSelected: boolean
): ButtonProps['sx'] {
  if (!isSelected) return undefined

  return {
    backgroundColor: getLightMuiBackgroundColor(muiTheme.palette.primary.light),
    '&:hover': {
      backgroundColor: getLightMuiBackgroundColorHover(
        muiTheme.palette.primary.light
      )
    }
  }
}

/**
 * Centralized mapping of common run statuses to their corresponding colors.
 * Maps statuses like 'passed', 'failed', 'unsure', and 'default' to theme colors.
 * The 'other' key is reserved for categories outside of these standard statuses,
 * like attempted for an RM service or no participation for LST.
 */
export const RUN_RESULT_COLORS = {
  default: muiTheme.palette.text.primary,
  failed: muiTheme.palette.error.main,
  other: muiTheme.palette.text.secondary,
  passed: muiTheme.palette.success.main,
  unsure: muiTheme.palette.warning.main
}
